// Lightweight GA4 wrapper. It no-ops entirely when VITE_GA_MEASUREMENT_ID is unset
// (local dev / no analytics configured) or when running outside a browser (SSR /
// build-time prerender), so the site works identically with or without analytics.
//
// To enable: set VITE_GA_MEASUREMENT_ID (e.g. "G-XXXXXXXXXX") at build time. The ID
// is public by design (it ships in the client bundle), so it lives in an env var,
// not a secret.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

const CONSENT_KEY = 'cryptperks_cookie_consent';
export type ConsentStatus = 'accepted' | 'rejected';

/** The visitor's stored cookie-consent choice, or null if they haven't decided yet. */
export function getConsent(): ConsentStatus | null {
  if (typeof window === 'undefined') return null;
  const value = localStorage.getItem(CONSENT_KEY);
  return value === 'accepted' || value === 'rejected' ? value : null;
}

/** Persists the visitor's choice and, if accepted, loads analytics immediately. */
export function setConsent(status: ConsentStatus): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(CONSENT_KEY, status);
  }
  if (status === 'accepted') {
    initAnalytics();
  }
}

function isEnabled(): boolean {
  return typeof window !== 'undefined' && !!GA_ID;
}

/**
 * Loads the GA4 script and configures the property. Safe to call multiple times.
 * Only call this once the visitor has accepted cookies (see getConsent/setConsent) —
 * it does not check consent itself, so callers are responsible for gating it.
 */
export function initAnalytics(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // SPA: we send page_view manually on each route change instead of once on load.
  window.gtag('config', GA_ID, { send_page_view: false });
}

/** Records a virtual page view for SPA route changes. */
export function trackPageView(path: string): void {
  if (!isEnabled() || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * Records a click on an outbound affiliate/exchange link — the core conversion
 * signal for this site. `location` identifies which button was clicked so we can
 * see which surfaces (home cards, exchange pages, guide CTAs) actually convert.
 */
export function trackAffiliateClick(params: {
  exchangeId: string;
  location: string;
  lang: string;
}): void {
  if (!isEnabled() || !window.gtag) return;
  window.gtag('event', 'affiliate_click', {
    exchange_id: params.exchangeId,
    click_location: params.location,
    language: params.lang,
  });
}
